import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Landmark } from "lucide-react";
import { useI18n } from "@/i18n";

const NAV_KEYS = ["services", "eligibility", "faq"];

export default function Header() {
  const { t, lang, switchLanguage, buildPath, otherLang } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const homePath = buildPath("home");

  const scrollToSection = (id) => {
    setOpen(false);
    // Only scroll if we're on home page
    const isHome =
      location.pathname === `/${lang}` || location.pathname === `/${lang}/`;
    if (isHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // Navigate to home and then scroll
      window.location.href = `${homePath}#${id}`;
    }
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full bg-[#0F2C59] text-white border-b border-[#1E3A8A] shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Brand */}
          <Link
            to={homePath}
            data-testid="navbar-logo"
            className="flex items-center gap-3 group"
          >
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-[#C5A059] text-[#0F2C59]">
              <Landmark size={22} strokeWidth={2.2} />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-serif text-xl font-semibold tracking-tight">
                {t("header.brand")}
              </span>
              <span className="hidden sm:block text-[11px] uppercase tracking-widest text-[#C5A059]">
                {t("header.tagline")}
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            <NavLink
              to={homePath}
              end
              data-testid="nav-link-home"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-white" : "text-slate-200 hover:text-white"}`
              }
            >
              {t("header.nav.home")}
            </NavLink>
            {NAV_KEYS.map((key) => (
              <button
                key={key}
                data-testid={`nav-link-${key}`}
                onClick={() => scrollToSection(key)}
                className="text-sm font-medium text-slate-200 hover:text-white transition-colors"
              >
                {t(`header.nav.${key}`)}
              </button>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              data-testid="language-switcher-button"
              onClick={() => switchLanguage(otherLang)}
              aria-label={t("language.aria")}
              className="inline-flex items-center gap-2 border border-white/30 text-white px-3 py-1.5 rounded-md text-xs font-semibold tracking-widest uppercase hover:bg-white hover:text-[#0F2C59] transition-colors"
            >
              <span className={lang === "mx" ? "opacity-100" : "opacity-50"}>
                {t("language.labelES")}
              </span>
              <span className="opacity-40">/</span>
              <span className={lang === "en" ? "opacity-100" : "opacity-50"}>
                {t("language.labelEN")}
              </span>
            </button>

            <button
              data-testid="header-cta-button"
              onClick={() => scrollToSection("newsletter")}
              className="hidden md:inline-flex items-center bg-[#C5A059] text-[#0F2C59] font-semibold text-sm px-4 py-2 rounded-md hover:bg-[#d4b56d] transition-colors"
            >
              {t("header.cta")}
            </button>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-white/30"
              aria-label={open ? t("header.closeMenu") : t("header.openMenu")}
              aria-expanded={open}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="lg:hidden border-t border-white/10 bg-[#0F2C59]"
        >
          <div className="px-4 py-4 flex flex-col gap-1">
            <NavLink
              to={homePath}
              end
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-home"
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium ${isActive ? "bg-white/10" : "hover:bg-white/5"}`
              }
            >
              {t("header.nav.home")}
            </NavLink>
            {NAV_KEYS.map((key) => (
              <button
                key={key}
                data-testid={`mobile-nav-${key}`}
                onClick={() => scrollToSection(key)}
                className="text-left px-3 py-2 rounded-md text-sm font-medium hover:bg-white/5"
              >
                {t(`header.nav.${key}`)}
              </button>
            ))}
            <button
              data-testid="mobile-cta-button"
              onClick={() => scrollToSection("newsletter")}
              className="mt-2 bg-[#C5A059] text-[#0F2C59] font-semibold text-sm px-4 py-2.5 rounded-md hover:bg-[#d4b56d] transition-colors"
            >
              {t("header.cta")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
